import { useMemo, useState } from 'react'
import Notification from '../../model/Notification'
import AppIcon from '../icons/AppIcon'
import NotificationItem from './NotificationItem'
import {
  filterNotifications,
  groupNotifications,
  toNotificationDisplay,
  type NotificationFilter,
} from './notificationDisplay'
import Tabs from '../primitives/Tabs'
import styles from './Notifications.module.css'

export interface NotificationListPanelProps {
  onClose: () => void
  notifications: Notification[]
  isLoading: boolean
  onMarkAsRead: (notification: Notification) => void
  onMarkAllAsRead: () => void
}

export default function NotificationListPanel({
  onClose,
  notifications,
  isLoading,
  onMarkAsRead,
  onMarkAllAsRead,
}: NotificationListPanelProps) {
  const [filter, setFilter] = useState<NotificationFilter>('unread')

  const displays = useMemo(
    () => notifications.map(toNotificationDisplay),
    [notifications],
  )
  const filtered = useMemo(() => filterNotifications(displays, filter), [displays, filter])
  const { unread: newItems, read: earlierItems } = useMemo(
    () => groupNotifications(filtered),
    [filtered],
  )

  const unreadCount = displays.filter((item) => !item.read).length
  const showEmpty = filter === 'unread' && filtered.length === 0 && !isLoading

  function renderSection(label: string, items: ReturnType<typeof toNotificationDisplay>[]) {
    if (items.length === 0) return null

    return (
      <>
        <div className={styles.sec}>
          <span className={styles.secEyebrow}>{label}</span>
          <span className={styles.secRule} />
        </div>
        {items.map((item) => {
          const notification = notifications.find((n) => n.id === item.id)
          if (!notification) return null
          return (
            <NotificationItem
              key={item.id}
              item={item}
              onSelect={() => onMarkAsRead(notification)}
            />
          )
        })}
      </>
    )
  }

  return (
    <div className={styles.panel}>
      <div className={styles.scroll}>
        <div className={styles.nav}>
          <button type="button" className={styles.back} onClick={onClose} title="Back">
            <AppIcon icon="mdi:chevron-left" size={22} />
          </button>
          <span className={styles.title}>
            Notifications
            {unreadCount > 0 && <span className={styles.count}>{unreadCount}</span>}
          </span>
          <button
            type="button"
            className={[styles.clear, unreadCount === 0 && styles.clearDisabled].filter(Boolean).join(' ')}
            onClick={() => onMarkAllAsRead()}
          >
            Mark all read
          </button>
        </div>

        <Tabs
          value={filter}
          onChange={setFilter}
          variant='pill'
          listClassName={styles.filters}
          items={[
            { value: 'all', label: 'All' },
            {
              value: 'unread',
              label: (
                <>
                  Unread
                  {unreadCount > 0 && <span className={styles.filterCount}> {unreadCount}</span>}
                </>
              ),
            },
            { value: 'team', label: 'Team' },
          ]}
        />

        <div className={styles.bodyPad}>
          {isLoading ? (
            <p className={styles.loading}>Loading…</p>
          ) : (
            <>
              {renderSection('New', newItems)}
              {renderSection('Earlier', earlierItems)}
              {showEmpty && (
                <div className={styles.empty}>
                  <div className={styles.emptyBadge}>
                    <AppIcon icon="mdi:check" size={26} />
                  </div>
                  <h3 className={styles.emptyTitle}>All caught up</h3>
                  <p className={styles.emptyText}>No unread notifications. Go log a few RMs.</p>
                </div>
              )}
              {!isLoading && filtered.length === 0 && filter !== 'unread' && (
                <div className={styles.empty}>
                  <h3 className={styles.emptyTitle}>No notifications</h3>
                  <p className={styles.emptyText}>You&apos;re all set for now.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
